import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import Agent from '../../entity/Agent';

class AgentWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ idGraphqlClient }: { idGraphqlClient: GraphQLClient }) {
    this.graphqlClient = idGraphqlClient;
  }

  public currentAgent = async (): Promise<{
    id: string;
    comment: string;
    name: string;
    permissions: { action: string; permission: boolean }[];
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query currentAgent {
          currentAgent {
            comment
            id
            name
            permissions {
              action
              permission
            }
          }
        }
      `,
    );
    return Agent.fromJSON(result.currentAgent) as Agent;
  };

  public updateAgent = async (
    id: string,
    name: string,
    comment: string,
  ): Promise<{
    id: string;
    comment: string;
    name: string;
    permissions: { action: string; permission: boolean }[];
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation updateAgent($id: ID!, $name: String!, $comment: String) {
          updateAgent(input: { id: $id, name: $name, comment: $comment }) {
            agent {
              id
              name
            }
            clientMutationId
          }
        }
      `,
      { id, name, comment },
    );
    return Agent.fromJSON(result.updateAgent.agent) as Agent;
  };

  public agentMemberships = async (
    agent_id: string,
  ): Promise<{
    nodes: {
      agent: Agent;
      id: string;
      roleNames: string[];
      user: {
        id: string;
        name: string;
        profileImageUrl: string;
      };
    }[];
    pageInfo: {
      endCursor: string;
      startCursor: string;
      hasPreviousPage: boolean;
      hasNextPage: boolean;
    };
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query agentMemberships($agent_id: ID!) {
          agentMemberships(agentId: $agent_id) {
            nodes {
              agent {
                comment
                id
                name
              }
              id
              roleNames
              user {
                profileImageUrl
                name
                id
              }
            }
            pageInfo {
              endCursor
              startCursor
              hasPreviousPage
              hasNextPage
            }
          }
        }
      `,
      { agent_id },
    );
    return result.agentMemberships;
  };

  public createAgentMembership = async (
    token: string,
  ): Promise<{
    agent: Agent | null;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation createAgentMembership($token: String!) {
          createAgentMembership(input: { token: $token }) {
            agent {
              comment
              id
              name
            }
            clientMutationId
          }
        }
      `,
      { token },
    );
    return result.createAgentMembership;
  };

  public updateAgentMembership = async (
    id: string,
    role_names: string[],
  ): Promise<{
    agentMembership: {
      agent: Agent | null;
      id: string;
      roleNames: string[];
      user: {
        id: string;
        name: string;
        profileImageUrl: string;
      };
    };
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation updateAgentMembership($id: ID!, $role_names: [String!]!) {
          updateAgentMembership(input: { id: $id, roleNames: $role_names }) {
            agentMembership {
              agent {
                id
              }
              id
              roleNames
              user {
                id
                profileImageUrl
                name
              }
            }
            clientMutationId
          }
        }
      `,
      { id, role_names },
    );
    return result.updateAgentMembership;
  };

  public deleteAgentMembership = async (
    id: string,
  ): Promise<{
    id: string;
    clientMutationId?: string;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation deleteAgentMembership($id: ID!) {
          deleteAgentMembership(input: { id: $id }) {
            id
            clientMutationId
          }
        }
      `,
      { id },
    );
    return result.deleteAgentMembership;
  };

  public agentInvitation = async (
    agentId: string,
  ): Promise<{
    agent: Agent;
    enabled: boolean;
    id: string;
    token: string;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query agentInvitation($agentId: ID!) {
          agentInvitation(agentId: $agentId) {
            agent {
              comment
              id
              name
            }
            enabled
            id
            token
          }
        }
      `,
      { agentId },
    );
    return result.agentInvitation;
  };

  public updateAgentInvitation = async (
    id: string,
    enabled: boolean,
  ): Promise<{
    agent: Agent;
    enabled: boolean;
    id: string;
    token: string;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation updateAgentInvitation($id: ID!, $enabled: Boolean!) {
          updateAgentInvitation(input: { id: $id, enabled: $enabled }) {
            agentInvitation {
              id
              enabled
              token
              agent {
                id
              }
            }
          }
        }
      `,
      { id, enabled },
    );
    return result.updateAgentInvitation;
  };

  public regenerateAgentInvitation = async (
    agentId: string,
  ): Promise<{
    agentInvitation: {
      agent: Agent;
      enabled: boolean;
      id: string;
      token: string;
    };
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation regenerateAgentInvitation($agentId: ID!) {
          regenerateAgentInvitation(input: { agentId: $agentId }) {
            agentInvitation {
              id
              enabled
              token
              agent {
                id
              }
            }
            clientMutationId
          }
        }
      `,
      { agentId },
    );
    return result.regenerateAgentInvitation;
  };

  public agent = async (
    invitationToken: string,
  ): Promise<{
    id: string;
    name: string;
  }> => {
    const result = await this.graphqlClient.query(
      gql`
        query agent($invitationToken: String!) {
          agent(invitationToken: $invitationToken) {
            id
            name
          }
        }
      `,
      { invitationToken },
    );
    return result.agent;
  };
}

export default AgentWebApi;
